import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, Paper, Avatar, Card, CardContent, TextField, InputAdornment, Container, Fade, Skeleton, Badge, Tooltip, IconButton, Chip, Button, FormControl, Select, MenuItem, InputLabel, LinearProgress,  } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBack,
  SearchRounded,
  TrendingUp,
  AccountBalance,
  Campaign,
  Handshake,
  AccessTime,
  Star,
  Verified,
  Instagram,
  YouTube,
  Speed,
  ChevronRight,
  FilterList,
  Refresh,
  Analytics,
  TrendingDown,
  TrendingFlat,
  Launch,
  Person,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDealFlowConnection, ACTIVITY_TYPES, PRIORITY_LEVELS } from './../Components/useFlowConnection';
import useAuth from '../../../Hooks/use-auth';
import { handleAvatarError } from '../../../Utils';
import profilePhoto from '../../../Components/globalAssets/ppfLogo.png';

const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  padding: theme.spacing(6, 0),
  borderRadius: '0 0 32px 32px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 20%, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 50%)`,
  },
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(145deg, 
    ${alpha(theme.palette.background.paper, 0.9)}, 
    ${alpha(theme.palette.background.paper, 0.7)}
  )`,
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.5)}, transparent)`,
  },
}));

const ActivityCard = styled(Card)(({ theme, isSelected, priority }) => ({
  borderRadius: '16px',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  border: isSelected 
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: isSelected
    ? alpha(theme.palette.primary.main, 0.05)
    : theme.palette.background.paper,
  borderLeft: `4px solid ${PRIORITY_LEVELS[priority]?.color || theme.palette.divider}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
    borderColor: theme.palette.primary.main,
  },
}));

const CreatorCard = styled(Card)(({ theme, isActive }) => ({
  borderRadius: '20px',
  transition: 'all 0.3s ease',
  border: isActive 
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: isActive 
    ? alpha(theme.palette.primary.main, 0.05)
    : theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  color: theme.palette.common.white,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '50%',
    height: '50%',
    background: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.1)} 0%, transparent 70%)`,
  },
}));

const getActivityIcon = (type) => {
  switch (type) {
    case 'Campaign': return <Campaign />;
    case 'Partnership': return <Handshake />;
    case 'Payout': return <AccountBalance />;
    default: return <AccessTime />;
  }
};

const getTrendIcon = (value, prevValue = 0) => {
  if (value > prevValue) return <TrendingUp color="success" />;
  if (value < prevValue) return <TrendingDown color="error" />;
  return <TrendingFlat color="warning" />;
};

export default function EnhancedAgencyDealFlow() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const agencyName = userInfo?.company_name;

  const {
    // State
    selectedCreator,
    selectedActivity,
    searchQuery,
    activityFilter,
    
    // Data
    activities,
    creators,
    creatorDetails,
    
    // Loading states
    feedLoading,
    creatorsLoading,
    creatorDetailsLoading,
    
    // Statistics
    creatorStats,
    activityStats,
    
    // Actions
    handleActivitySelect,
    handleCreatorSelect,
    clearSelections,
    updateSearch,
    updateActivityFilter,
    nextPage,
    prevPage,
    
    // Utilities
    getCreatorActivities,
    getCreatorMetrics,
  } = useDealFlowConnection();

  const creatorMetrics = selectedCreator ? getCreatorMetrics(selectedCreator) : null;
  const creatorActivities = selectedCreator ? getCreatorActivities(selectedCreator) : [];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  onClick={() => navigate('/agency')}
                  sx={{ 
                    mr: 2, 
                    color: 'white',
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.2) }
                  }}
                >
                  <ArrowBack />
                </IconButton>
                <Typography variant="h3" sx={{ 
                  fontWeight: 900, 
                  color: 'white',
                  letterSpacing: '-0.02em',
                  mr: 2,
                }}>
                  Deal Flow
                </Typography>
                <Chip 
                  icon={<Speed />}
                  label="Live"
                  sx={{ 
                    bgcolor: alpha(theme.palette.success.main, 0.2),
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Box>
              
              <Button
                variant="outlined"
                startIcon={<Analytics />}
                onClick={() => navigate('/dashboard')}
                sx={{
                  color: 'white',
                  borderColor: alpha(theme.palette.common.white, 0.3),
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                  },
                }}
              >
                Full Analytics
              </Button>
            </Box>

            {/* Quick Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={3}>
                <StatCard>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={800}>
                      {activityStats.total}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Activities
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={3}>
                <StatCard>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={800}>
                      ${Math.round(activityStats.totalRevenue / 1000)}K
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Revenue
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={3}>
                <StatCard>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={800}>
                      {creatorStats.active}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Active Creators
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={3}>
                <StatCard>
                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={800}>
                      ${Math.round(activityStats.avgDealSize)}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Avg Deal
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ 
              color: alpha(theme.palette.common.white, 0.9),
              maxWidth: 600,
              lineHeight: 1.6,
            }}>
              Monitor real-time creator activities, partnerships, and revenue streams. Click on activities to view detailed creator information.
            </Typography>
          </Box>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ mt: -4, pb: 6, position: 'relative', zIndex: 3 }}>
        <Grid container spacing={4}>
          {/* Activity Feed Panel */}
          <Grid item xs={12} md={5}>
            <GlassCard sx={{ height: 'calc(100vh - 300px)', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h5" fontWeight={700}>
                    Activity Feed
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Badge badgeContent={activities.length} color="primary">
                      <TrendingUp />
                    </Badge>
                    <IconButton size="small" onClick={() => window.location.reload()}>
                      <Refresh />
                    </IconButton>
                  </Box>
                </Box>

                {/* Activity Filter */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Filter Activities</InputLabel>
                  <Select
                    value={activityFilter}
                    onChange={(e) => updateActivityFilter(e.target.value)}
                    startAdornment={<FilterList sx={{ mr: 1, color: 'text.secondary' }} />}
                  >
                    <MenuItem value="all">All Activities</MenuItem>
                    <MenuItem value="Campaign">Campaigns</MenuItem>
                    <MenuItem value="Partnership">Partnerships</MenuItem>
                    <MenuItem value="Payout">Payouts</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {feedLoading ? (
                  [...Array(5)].map((_, index) => (
                    <Card key={index} sx={{ mb: 2, borderRadius: '16px' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Skeleton variant="circular" width={48} height={48} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton width="60%" />
                            <Skeleton width="40%" />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  activities.map((activity, index) => {
                    const isSelected = selectedActivity?.id === activity.id;
                    
                    return (
                      <Fade in={true} timeout={300 + index * 100} key={activity.id}>
                        <ActivityCard 
                          isSelected={isSelected}
                          priority={activity.priority}
                          onClick={() => handleActivitySelect(activity.rawActivity)}
                        >
                          <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar
                                src={activity.avatar || profilePhoto}
                                onError={handleAvatarError(profilePhoto)}
                                sx={{
                                  width: 48,
                                  height: 48,
                                  border: `2px solid ${ACTIVITY_TYPES[activity.type]?.color || theme.palette.primary.main}`,
                                }}
                              >
                                {getActivityIcon(activity.type)}
                              </Avatar>
                              
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                                    {activity.title}
                                  </Typography>
                                  <Chip 
                                    label={activity.type}
                                    size="small"
                                    sx={{ 
                                      bgcolor: ACTIVITY_TYPES[activity.type]?.color,
                                      color: 'white',
                                      fontWeight: 600,
                                    }}
                                  />
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  @{activity.creator} • {activity.subtitle}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                  <Typography variant="body2" fontWeight={700} color="primary">
                                    ${activity.price.toLocaleString()}
                                  </Typography>
                                  <Chip 
                                    label={PRIORITY_LEVELS[activity.priority]?.label}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha(PRIORITY_LEVELS[activity.priority]?.color, 0.1),
                                      color: PRIORITY_LEVELS[activity.priority]?.color,
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {activity.date}
                                  </Typography>
                                </Box>
                              </Box>
                              
                              <ChevronRight color="action" />
                            </Box>
                          </CardContent>
                        </ActivityCard>
                      </Fade>
                    );
                  })
                )}
              </Box>
            </GlassCard>
          </Grid>

          {/* Creator Management Panel */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={3}>
              {/* Creator Search & List */}
              <Grid item xs={12} md={6}>
                <GlassCard sx={{ height: 'calc(100vh - 300px)', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Creator Roster ({creatorStats.total})
                    </Typography>
                    <TextField
                      value={searchQuery}
                      onChange={(e) => updateSearch(e.target.value)}
                      placeholder="Search creators..."
                      size="small"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRounded />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: '12px',
                          bgcolor: alpha(theme.palette.background.paper, 0.5),
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {creatorsLoading ? (
                      [...Array(5)].map((_, index) => (
                        <Card key={index} sx={{ mb: 2, borderRadius: '12px' }}>
                          <CardContent sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Skeleton variant="circular" width={40} height={40} />
                              <Box sx={{ flex: 1 }}>
                                <Skeleton width="70%" />
                                <Skeleton width="50%" />
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      creators.map((creator, index) => (
                        <Fade in={true} timeout={200 + index * 50} key={creator.creator}>
                          <CreatorCard 
                            isActive={selectedCreator === creator.creator}
                            onClick={() => handleCreatorSelect(creator.creator)}
                            sx={{ mb: 2, cursor: 'pointer' }}
                          >
                            <CardContent sx={{ py: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                  src={creator.pfphref || profilePhoto}
                                  onError={handleAvatarError(profilePhoto)}
                                  sx={{ width: 40, height: 40 }}
                                >
                                  {creator.creator.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                                    @{creator.display_name || creator.creator}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {creator.notes_content_style || 'Creator'}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Verified sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                                  {getCreatorActivities(creator.creator).length > 0 && (
                                    <Badge 
                                      badgeContent={getCreatorActivities(creator.creator).length}
                                      color="primary"
                                      sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}
                                    >
                                      <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                                    </Badge>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </CreatorCard>
                        </Fade>
                      ))
                    )}
                  </Box>
                </GlassCard>
              </Grid>

              {/* Creator Profile Detail */}
              <Grid item xs={12} md={6}>
                <GlassCard sx={{ height: 'calc(100vh - 300px)', display: 'flex', flexDirection: 'column' }}>
                  {selectedCreator ? (
                    creatorDetailsLoading ? (
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                          <Skeleton variant="circular" width={80} height={80} sx={{ mx: 'auto', mb: 2 }} />
                          <Skeleton width="60%" height={32} sx={{ mx: 'auto', mb: 1 }} />
                          <Skeleton width="40%" height={20} sx={{ mx: 'auto' }} />
                        </Box>
                        {[...Array(4)].map((_, i) => (
                          <Skeleton key={i} height={40} sx={{ mb: 2, borderRadius: '8px' }} />
                        ))}
                      </Box>
                    ) : creatorDetails && creatorMetrics ? (
                      <Box sx={{ p: 3, overflow: 'auto' }}>
                        {/* Creator Header */}
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                          <Avatar
                            src={creatorDetails.pfphref || profilePhoto}
                            onError={handleAvatarError(profilePhoto)}
                            sx={{
                              width: 80,
                              height: 80,
                              mx: 'auto',
                              mb: 2,
                              border: `3px solid ${theme.palette.primary.main}`,
                            }}
                          >
                            {selectedCreator.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="h6" fontWeight={700} gutterBottom>
                            @{creatorDetails.display_name || selectedCreator}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                            <Chip 
                              icon={<Star />}
                              label="Active Creator"
                              color="primary"
                              size="small"
                            />
                            {creatorMetrics.engagementScore > 75 && (
                              <Chip 
                                label="Top Performer"
                                size="small"
                                sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main' }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Engagement Score */}
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              Engagement Score
                            </Typography>
                            <Typography variant="body2" color="primary" fontWeight={700}>
                              {creatorMetrics.engagementScore}/100
                            </Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={creatorMetrics.engagementScore}
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                            }}
                          />
                        </Box>

                        {/* Platform Stats */}
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Platform Presence
                        </Typography>
                        
                        {creatorMetrics.platforms.youtube > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: alpha('#FF0000', 0.1), borderRadius: '12px' }}>
                            <YouTube sx={{ color: '#FF0000' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>YouTube</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {creatorMetrics.platforms.youtube.toLocaleString()} subscribers
                              </Typography>
                            </Box>
                            {getTrendIcon(creatorMetrics.platforms.youtube)}
                          </Box>
                        )}

                        {creatorMetrics.platforms.instagram > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: alpha('#E4405F', 0.1), borderRadius: '12px' }}>
                            <Instagram sx={{ color: '#E4405F' }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>Instagram</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {creatorMetrics.platforms.instagram.toLocaleString()} followers
                              </Typography>
                            </Box>
                            {getTrendIcon(creatorMetrics.platforms.instagram)}
                          </Box>
                        )}

                        {creatorMetrics.platforms.tiktok > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: alpha('#000000', 0.1), borderRadius: '12px' }}>
                            <Box sx={{ width: 24, height: 24, bgcolor: '#000000', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Typography variant="caption" sx={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>T</Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>TikTok</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {creatorMetrics.platforms.tiktok.toLocaleString()} followers
                              </Typography>
                            </Box>
                            {getTrendIcon(creatorMetrics.platforms.tiktok)}
                          </Box>
                        )}

                        {/* Performance Metrics */}
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                          <Grid item xs={6}>
                            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: '12px', textAlign: 'center' }}>
                              <Typography variant="h6" color="primary" fontWeight={800}>
                                {creatorMetrics.totalReach.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Total Reach
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: '12px', textAlign: 'center' }}>
                              <Typography variant="h6" color="success.main" fontWeight={800}>
                                ${creatorMetrics.totalRevenue.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Revenue Generated
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Recent Activities */}
                        {creatorActivities.length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                              Recent Activities ({creatorActivities.length})
                            </Typography>
                            {creatorActivities.slice(0, 3).map((activity, index) => (
                              <Box key={activity.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 1, bgcolor: alpha(theme.palette.background.paper, 0.5), borderRadius: '8px' }}>
                                <Box sx={{ color: ACTIVITY_TYPES[activity.type]?.color }}>
                                  {getActivityIcon(activity.type)}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {activity.type}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ${activity.price.toLocaleString()}
                                  </Typography>
                                </Box>
                                <IconButton size="small" onClick={() => navigate(activity.link)}>
                                  <Launch fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<Person />}
                            onClick={() => navigate(`/agency/creators/${selectedCreator}`)}
                          >
                            View Profile
                          </Button>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={clearSelections}
                          >
                            Clear
                          </Button>
                        </Box>
                      </Box>
                    ) : null
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', p: 3 }}>
                      <Box>
                        <TrendingUp sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          Select an Activity or Creator
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Choose an activity from the feed or select a creator to view detailed information and metrics
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </GlassCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}